import { maskPathIds, reportClientError, reportClientMessage, stripQuery } from '@utils/clientErrors'
import { queryClient } from '@services/query'
import axios, { AxiosError } from 'axios'
import axiosRetry from 'axios-retry'
import { getToken, useAuthState } from 'store/auth'
import { CONNECTION_ERROR_MSG, getSafeErrorString, isDOMExceptionGetterCrash } from 'utils/httpError'
import { isValidJWT } from './client/patient'

const URLS_TO_IGNORE = ['https://api.ideal-postcodes.co.uk', 'directory.spineservices.nhs.uk']

const http = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  timeout: +process.env.NEXT_PUBLIC_API_TIMEOUT,
  timeoutErrorMessage: 'Request timed out',
  ...(process.env.NEXT_PUBLIC_VERCEL_ENV === 'local' && {
    httpsAgent: new (require('https').Agent)({
      rejectUnauthorized: false
    })
  }),
  headers: {
    'User-Agent': typeof window === 'undefined' ? 'Connect' : undefined
  }
})

http.interceptors.request.use(async req => {
  if (typeof window !== 'undefined') {
    const vendor = window.location.pathname.split('/')[2]
    if (vendor) {
      req.headers['x-evaro-vendor'] = vendor
    }
  }

  if (['/authenticate/token/renew'].some(url => req.url.includes(url))) return req
  if (useAuthState.getState().isLogged && req.headers.Authorization !== null) {
    const token = await getToken()
    if (!token) {
      throw new AxiosError(`No Token, cancelling request ${req.url}`, 'ERR_NO_TOKEN')
    }
    req.headers.Authorization = `Bearer ${token}`
  }

  return req
})

http.interceptors.response.use(
  res => res.data?.data ?? res.data,
  error => {
    if (error.code === 'ERR_NO_TOKEN') {
      return Promise.reject(new Error(CONNECTION_ERROR_MSG))
    }

    const status = error.response?.status
    const url: string = error.config?.url
    const errorMessage = getSafeErrorString(error, 'message')
    console.warn('HTTP API ERROR', url, error, errorMessage)

    // DOMException handling: On Safari, axios AxiosError.from() crashes when copying
    // DOMException prototype getters, producing a TypeError with this specific message.
    // Catch it early and return a user-friendly error.
    if (isDOMExceptionGetterCrash(error)) {
      reportClientError(error, { tags: { error_type: 'DOMException_Safari' } })
      return Promise.reject(new Error(CONNECTION_ERROR_MSG))
    }

    if (axios.isCancel(error)) return Promise.reject(new Error(CONNECTION_ERROR_MSG))
    // TIMEOUT
    if (error.code === 'ECONNABORTED') return Promise.reject(new Error(CONNECTION_ERROR_MSG))
    // NO STATUS OR URL NETWORK ISSUE
    if (!status || !url) {
      reportClientError(error, {
        tags: { error_type: 'network_no_response', axios_code: error.code ?? 'unknown', axios_name: error.name ?? 'unknown' },
        extra: { method: error.config?.method, url: maskPathIds(stripQuery(error.config?.url ?? '')), retryCount: error.config?.['axios-retry']?.retryCount }
      })
      return Promise.reject(new Error(CONNECTION_ERROR_MSG))
    }

    const isFEError = status >= 400 && status < 500

    if (status === 403) {
      console.warn('403 Error, logging out')
      useAuthState.getState().logOut()
      queryClient.removeQueries({ queryKey: ['user'], type: 'all' })
      // Return early so an expired or invalid token isn't reported as an error
      return Promise.reject(error.response?.data?.data || error.response?.data)
    }

    try {
      const ignoreUrl = URLS_TO_IGNORE.some(e => url.includes(e))
      if (ignoreUrl) return Promise.reject(error)
      const hashedUrl = url
        .split('/')
        .map(e => (e.length === 30 ? ':hash' : e))
        .join('/')
      error.name = 'API Error'
      error.message = `URL:${hashedUrl} ${errorMessage || 'Unknown error'}`

      reportClientMessage(`API Error : ${error.config.method.toUpperCase()} ${maskPathIds(stripQuery(hashedUrl))} ${status}`, {
        level: isFEError ? 'info' : 'error',
        tags: typeof error.response?.data?.code === 'string' ? { API_ERROR_CODE: error.response.data.code.slice(0, 100) } : undefined,
        extra: {
          hasJWT: !!error.config.headers?.Authorization,
          isValidJWT: !!isValidJWT(error.config.headers?.Authorization as string),
          retryCount: error.config['axios-retry']?.retryCount,
          code: error.code
        }
      })
    } catch (e) {
      console.error('Error in HTTP response error try method', e)
      reportClientError(e, { tags: { source: 'http-interceptor' } })
    }

    return Promise.reject(error.response?.data?.data || error.response?.data)
  }
)

axiosRetry(http, {
  retries: 3,
  shouldResetTimeout: true,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition(error) {
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || error.code === 'ECONNABORTED'
  }
  // onRetry(retryCount, error, requestConfig) {
  //   const hashedUrl = requestConfig.url.split('/').map(e => e.length === 30 ? ':hash' : e).join('/')

  //   if (retryCount === 1) {
  //     Sentry.captureMessage('API Retry attempt', {
  //       level: 'debug',
  //       contexts: {
  //         http_error: {
  //           ...error
  //         }
  //       },
  //       tags: {
  //         API_URL: `${error.config.method.toUpperCase()} ${hashedUrl}`,
  //       }
  //     })
  //   }
  // },
})

export default http
